import Layout from "@/components/feature/Layout";
import { usePageMeta } from "@/hooks/usePageMeta";
import CategoryShell from "@/components/feature/CategoryShell";
import AnimeCard from "@/components/base/AnimeCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { useCategoryFilter } from "@/hooks/useCategoryFilter";
import { animeList } from "@/mocks/anime";
import type { Anime } from "@/types";

const filterGroups = [
  {
    key: "genre",
    title: "按类型",
    options: ["科幻", "奇幻", "热血", "冒险", "治愈", "日常", "悬疑", "恋爱", "机战", "战斗"],
  },
  { key: "year", title: "按年代", options: ["2025", "2024", "2023"] },
  { key: "region", title: "按地区", options: ["日本", "中国", "韩国", "欧美"] },
];

const sortOptions = [
  {
    label: "最新",
    compare: (a: Anime, b: Anime) =>
      new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime(),
  },
  { label: "热门", compare: (a: Anime, b: Anime) => b.views - a.views },
  { label: "评分", compare: (a: Anime, b: Anime) => b.rating - a.rating },
];

export default function RecommendPage() {
  usePageMeta({
    title: "动画推荐 - 风车动漫网",
    description:
      "风车动漫网动画推荐频道精选科幻、奇幻、热血、治愈等多种题材的高口碑动画，支持按类型、年代、地区筛选，快速找到你想看的作品。",
    keywords: "动画推荐,动漫推荐,高口碑动画,风车动漫网",
  });

  const state = useCategoryFilter<Anime>(
    animeList,
    (anime) => ({ genre: anime.genre, year: [anime.year], region: [anime.region] }),
    sortOptions,
    6,
  );

  return (
    <Layout>
      <CategoryShell
        crumbs={[{ label: "动画推荐" }]}
        eyebrow="Anime Recommend"
        title="动画推荐"
        icon="ri-film-line"
        description="精选高口碑动画作品，涵盖科幻、奇幻、热血、治愈等多种题材。通过类型、年代与地区筛选，快速找到你想看的那一部。"
        filterGroups={filterGroups}
        selected={state.selected}
        onToggle={state.toggle}
        onReset={state.reset}
        sortOptions={sortOptions.map((option) => option.label)}
        sort={state.sort}
        onSort={state.setSort}
        resultLabel={`共 ${state.total} 部动画`}
      >
        {state.total === 0 ? (
          <EmptyState
            title="没有找到符合条件的动画"
            description="试试放宽筛选条件，或一键重置查看全部动画推荐。"
            action={
              <button
                type="button"
                onClick={state.reset}
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选
              </button>
            }
          />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4">
              {state.pageItems.map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
            </div>
            <Pagination current={state.page} total={state.totalPages} onChange={state.setPage} />
          </>
        )}
      </CategoryShell>
    </Layout>
  );
}